import gql from 'graphql-tag';
import { List } from 'immutable';

import GraphQLClient, { GraphQLOperationFailed } from '../../adapter/GraphQLClient';
import FacebookAccessToken from '../../entity/FacebookAccessToken';
import FacebookAdAccount, { MyRole } from '../../entity/FacebookAdAccount';
import FacebookIntegratedAdAccount from '../../entity/FacebookIntegratedAdAccount';
import { Creative, CreativeForCreationDetailFragment } from './../../generated/graphql';
import { creativeForCreationDetailFragment } from './CreationWebApi';

class FacebookAdWebApi {
  private readonly graphqlClient: GraphQLClient;
  private cachedAdAccounts: List<FacebookAdAccount> | null = null;
  private cachedIntegratedAdAccounts: List<FacebookIntegratedAdAccount> | null = null;
  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getAllAccessibleAdAccounts = async (
    facebookAccessToken: FacebookAccessToken,
  ): Promise<List<FacebookAdAccount>> => {
    if (this.cachedAdAccounts && this.cachedAdAccounts.size > 0) {
      return this.cachedAdAccounts;
    }

    let result: any;

    try {
      result = await this.graphqlClient.query(
        gql`
          query getFacebookAdAccounts($facebookAccessToken: ID!) {
            facebookAdAccounts(facebookAccessToken: $facebookAccessToken) {
              id
              accountId
              name
              business {
                id
                name
              }
              myRole
            }
          }
        `,
        { facebookAccessToken },
      );
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'UnauthenticatedError') {
        throw new FacebookOperationUnauthorized();
      }

      throw err;
    }

    // Sort accounts by user role
    const activeAdAccounts = result.facebookAdAccounts.filter(
      (account: any) => account.business && account.myRole !== MyRole.REPORTS_ONLY,
    );
    const readonlyAdAccounts = result.facebookAdAccounts.filter(
      (account: any) => account.business && account.myRole === MyRole.REPORTS_ONLY,
    );

    this.cachedAdAccounts = List([...activeAdAccounts, ...readonlyAdAccounts]).map((account: any) =>
      FacebookAdAccount.fromJSON(account),
    );

    return this.cachedAdAccounts;
  };

  // 連携済みのアカウントを取得
  public getAllIntegratedAdAccounts = async (organizationId: string): Promise<List<FacebookIntegratedAdAccount>> => {
    let result: any;

    try {
      result = await this.graphqlClient.query(
        gql`
          query integratedFacebookAdAccounts($organizationId: ID!) {
            integratedFacebookAdAccounts(organizationId: $organizationId) {
              id
              accountId
              name
              myRole
            }
          }
        `,
        { organizationId },
      );
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'UnauthenticatedError') {
        throw new FacebookOperationUnauthorized();
      }
      if (err instanceof GraphQLOperationFailed && err.type === 'FbAdAccountAlreadyIntegratedError') {
        throw new FbAdAccountAlreadyIntegrated();
      }
      throw err;
    }

    this.cachedIntegratedAdAccounts = List([...result.integratedFacebookAdAccounts]).map((account: any) =>
      FacebookIntegratedAdAccount.fromJSON(account),
    );

    return this.cachedIntegratedAdAccounts;
  };

  public sendCreativeToFacebookAdAccount = async ({
    creative,
    facebookAdAccount,
    facebookAccessToken,
  }: {
    creative: Pick<Creative, 'id'>;
    facebookAdAccount: FacebookAdAccount;
    facebookAccessToken: FacebookAccessToken;
  }): Promise<CreativeForCreationDetailFragment> => {
    let result: any;

    try {
      result = await this.graphqlClient.query(
        gql`
          ${creativeForCreationDetailFragment}
          mutation sendCreativeToFacebookAdAccount(
            $creativeId: ID!
            $facebookAdAccountId: ID!
            $facebookAccessToken: ID!
          ) {
            sendCreativeToFacebookAdAccount(
              creativeId: $creativeId
              facebookAdAccountId: $facebookAdAccountId
              facebookAccessToken: $facebookAccessToken
            ) {
              ...CreativeForCreationDetail
            }
          }
        `,
        {
          creativeId: creative.id,
          facebookAdAccountId: facebookAdAccount.id,
          facebookAccessToken,
        },
      );
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'UnauthenticatedError') {
        throw new FacebookOperationUnauthorized();
      }

      throw err;
    }

    return result.sendCreativeToFacebookAdAccount as CreativeForCreationDetailFragment;
  };

  // 新しく連携する
  public integrateAdAccount = async ({
    organizationId,
    facebookAdAccountId,
    facebookAccessToken,
  }: {
    organizationId: string;
    facebookAdAccountId: string;
    facebookAccessToken: string;
  }): Promise<void> => {
    try {
      await this.graphqlClient.query(
        gql`
          mutation integrateFacebookAdAccount(
            $organizationId: ID!
            $facebookAdAccountId: ID!
            $facebookAccessToken: ID!
          ) {
            integrateFacebookAdAccount(
              organizationId: $organizationId
              facebookAdAccountId: $facebookAdAccountId
              facebookAccessToken: $facebookAccessToken
            )
          }
        `,
        { organizationId, facebookAdAccountId, facebookAccessToken },
      );
    } catch (err) {
      if (err instanceof GraphQLOperationFailed && err.type === 'UnauthenticatedError') {
        throw new FacebookOperationUnauthorized();
      }
      if (err instanceof GraphQLOperationFailed && err.type === 'FbAdAccountAlreadyIntegratedError') {
        throw new FbAdAccountAlreadyIntegrated();
      }
      throw err;
    }
    return;
  };

  public revokeAdAccount = async ({
    organizationId,
    facebookAdAccountId,
  }: {
    organizationId: string;
    facebookAdAccountId: string;
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation revokeFacebookAdAccount($organizationId: ID!, $facebookAdAccountId: ID!) {
          revokeFacebookAdAccount(organizationId: $organizationId, facebookAdAccountId: $facebookAdAccountId)
        }
      `,
      { organizationId, facebookAdAccountId },
    );
    return;
  };
}

export class FacebookOperationUnauthorized extends Error {
  public readonly name = 'FacebookOperationUnauthorized';
  public readonly message = 'The operation to Facebook is unauthorized. (Not enough permissions)';

  constructor() {
    super();
  }
}

export class FbAdAccountAlreadyIntegrated extends Error {
  public readonly name = 'FbAdAccountAlreadyIntegrated';
  public readonly message = 'The operation to Facebook is already integrated.';
  constructor() {
    super();
  }
}

export default FacebookAdWebApi;
